/**
 * @fileoverview TrackSwitcherButton — labeled, small full-width select-style
 * button used inside the UserAccountMenu for the "Platform version" →
 * "Stable" track picker. Renders a label above a `bp6-button bp6-fill
 * bp6-align-left bp6-small` with a caret-down rightIcon. Generic enough to
 * reuse for any "labeled small select" inside a menu/popover (env switcher,
 * region picker, etc.).
 *
 * Foundry mapping: `user-account-menu__labeled-buttons-container__*` +
 * `__label__*` wrapping a Blueprint small button with caret-down.
 */

import { Button } from "@blueprintjs/core";
import type { ReactElement } from "react";

interface TrackSwitcherButtonProps {
  /** Field label rendered above the button. */
  label: string;
  /** Current selection text inside the button. */
  value: string;
  /** Opens the underlying menu / popover. */
  onClick?: () => void;
  /** Forwarded to the button for Popover-target wiring. */
  id?: string;
  ariaHaspopup?: "menu" | "listbox" | "true" | "false";
  ariaExpanded?: boolean;
  className?: string;
}

export function TrackSwitcherButton({
  label,
  value,
  onClick,
  id,
  ariaHaspopup = "menu",
  ariaExpanded = false,
  className,
}: TrackSwitcherButtonProps): ReactElement {
  return (
    <div data-testid="track-switcher-button"
      className={["bp-track-switcher-button", className].filter(Boolean).join(" ")}
    >
      <label className="bp-track-switcher-button-label" htmlFor={id}>
        {label}
      </label>
      <Button
        id={id}
        small
        fill
        alignText="left"
        rightIcon="caret-down"
        onClick={onClick}
        aria-haspopup={ariaHaspopup}
        aria-expanded={ariaExpanded}
        text={value}
      />
    </div>
  );
}
